// Mapping Template Service - Uses localStorage for client-side storage

export interface MappingTemplate {
    id: string;
    name: string;
    mappings: Array<{
        csvColumn: string;
        participantField: string;
    }>;
    created: string;
    lastUsed: string;
}

const STORAGE_KEY = 'import_mapping_templates';

// Get all saved templates
export const getTemplates = (): MappingTemplate[] => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch (error) {
        console.error('Error loading templates:', error);
        return [];
    }
};

// Save a new template
export const saveTemplate = (name: string, mappings: Array<{ csvColumn: string; participantField: string }>): MappingTemplate => {
    const templates = getTemplates();
    const newTemplate: MappingTemplate = {
        id: `template_${Date.now()}`,
        name,
        mappings,
        created: new Date().toISOString(),
        lastUsed: new Date().toISOString(),
    };

    templates.push(newTemplate);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));

    return newTemplate;
};

// Update template's last used time
export const updateTemplateLastUsed = (templateId: string): void => {
    const templates = getTemplates();
    const template = templates.find(t => t.id === templateId);
    if (template) {
        template.lastUsed = new Date().toISOString();
        localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
    }
};

// Delete a template
export const deleteTemplate = (templateId: string): void => {
    const templates = getTemplates();
    const filtered = templates.filter(t => t.id !== templateId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
};

// Rename a template
export const renameTemplate = (templateId: string, newName: string): void => {
    const templates = getTemplates();
    const template = templates.find(t => t.id === templateId);
    if (template) {
        template.name = newName;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
    }
};

// Get a specific template by id
export const getTemplate = (templateId: string): MappingTemplate | undefined => {
    const templates = getTemplates();
    return templates.find(t => t.id === templateId);
};

// Check if template name already exists
export const templateNameExists = (name: string, excludeId?: string): boolean => {
    const templates = getTemplates();
    return templates.some(t => t.name.toLowerCase() === name.toLowerCase() && t.id !== excludeId);
};
